/**
 * Frontend mirror of backend `utils/scope.py::workflow_template_task_ids_for_user`.
 *
 * Returns true if the given userId is effectively involved in the task,
 * accounting for per-task overrides (`task.manualOverrides`) and runtime
 * reassignments — so that the TaskBoard filter result matches what the
 * backend RBAC scope produces.
 */
export function isUserEffectivelyOnTask(task, workflows, userId) {
  if (!task || !userId) return false;

  // Strict matches first (cheapest, most common)
  if (task.assignedTo === userId) return true;
  if (task.completedFor && task.completedFor.includes(userId)) return true;
  if (task.assignmentHistory && task.assignmentHistory.some(a => a.userId === userId)) return true;
  if (task.createdBy === userId) return true;

  // Workflow template fallback — only valid if the template assignment
  // hasn't been overridden or already handled by someone else.
  const wf = workflows && workflows.find(w => w.id === task.workflowId);
  if (!wf) return false;

  const templateLocations = (wf.steps || [])
    .map((s, i) => ({ step: s, index: i }))
    .filter(({ step }) => step.assignToType === 'user' && step.assignToId === userId);

  if (!templateLocations.length) return false;

  const overrides = task.manualOverrides || {};
  const historyByName = {};
  for (const h of task.assignmentHistory || []) {
    const name = h.stepName;
    if (!name) continue;
    if (!historyByName[name]) historyByName[name] = [];
    historyByName[name].push(h.userId);
  }

  for (const { step, index } of templateLocations) {
    // 1) Per-task manual override (future step reassignment)
    const overrideId = overrides[String(index)];
    if (overrideId && overrideId !== userId) {
      continue; // reassigned away from user
    }
    // 2) Runtime history
    const stepName = step.stepName;
    if (stepName && historyByName[stepName]) {
      const users = historyByName[stepName];
      if (users.includes(userId)) return true;
      continue; // someone else handled this step
    }
    // 3) No override + no history.
    // If the step is already in the past (currentStepIndex moved beyond),
    // it was skipped without the user — exclude.
    const currentIndex = typeof task.currentStepIndex === 'number' ? task.currentStepIndex : 0;
    if (index < currentIndex) {
      continue;
    }
    return true; // step is current or future → user is expected handler
  }
  return false;
}
